import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { authAPI } from '../utils/apiService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem('authToken'));
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const currentToken = localStorage.getItem('authToken');
    if (currentToken) {
      setToken(currentToken);
      // Load user profile when token is available
      loadUserProfile();
    } else {
      setToken(null); // Ensure state is null if no token
    }
  }, []); // Runs once on mount to initialize token state

  const loadUserProfile = async () => {
    try {
      const userData = await authAPI.getUserProfile();
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user profile:', error);
      // If profile loading fails, user might have invalid token
      // Let the global 401 handler deal with it
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    navigate('/login');
  };

  // Set up global 401 handler
  useEffect(() => {
    apiClient.setUnauthorizedHandler(logout);
  }, []);

  const login = async (newToken, redirectPath = '/dashboard') => {
    localStorage.setItem('authToken', newToken);
    setToken(newToken);
    // Load user profile after successful login
    await loadUserProfile();
    navigate(redirectPath);
  };

  const setAuthToken = async (newToken) => {
    localStorage.setItem('authToken', newToken);
    setToken(newToken);
    // Load user profile after setting token
    await loadUserProfile();
  };

  // This effect listens for changes to localStorage from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'authToken') {
        setToken(event.newValue);
        if (!event.newValue) { // If token removed, navigate to login
          navigate('/login');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate]);


  return (
    <AuthContext.Provider value={{ token, user, setUser, login, logout, setAuthToken, isAuthenticated: !!token }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
}; 